// @ts-check

import {
  createIslandLayer,
  createAssignedIslandLayer,
  createIslandLabelLayer
} from "./mapboxLayers.js";
import {
  setupIslandClickHandler,
  setupIslandHoverHandlers
} from "./mapboxEvents.js";
import {
  filterIslandFeatures,
  matchIslandWithCsvData
} from "./geoJsonProcessing.js";

/**
 * Mapbox source and layer management.
 * Handles adding, removing, and updating map sources and layers.
 */

/**
 * Remove existing layer(s) and source from map.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string|string[]} layerIds - Layer ID(s) to remove
 * @param {string} sourceId - Source ID to remove
 */
export function removeExistingLayer(map, layerIds, sourceId) {
  if (!map.getSource(sourceId)) return;

  const layers = Array.isArray(layerIds) ? layerIds : [layerIds];
  layers.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  map.removeSource(sourceId);
}

/**
 * Set visibility for a single layer.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to update
 * @param {boolean|"visible" | "none"} visibility - Visibility state (boolean or "visible"/"none")
 */
export function setLayerVisibility(map, layerId, visibility) {
  if (!map.getLayer(layerId)) return;

  const visibilityValue =
    typeof visibility === "boolean"
      ? visibility
        ? "visible"
        : "none"
      : visibility;

  map.setLayoutProperty(layerId, "visibility", visibilityValue);
}

/**
 * Set visibility for multiple layers at once.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string[]} layerIds - Array of layer IDs to update
 * @param {boolean} visible - Whether layers should be visible
 */
export function setMultipleLayerVisibility(map, layerIds, visible) {
  const visibility = visible ? "visible" : "none";
  layerIds.forEach((layerId) => {
    setLayerVisibility(map, layerId, visibility);
  });
}

/**
 * Load a custom gold star icon into the map.
 * Called once when map loads, then reused for all layers.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @returns {Promise<void>}
 */
export function loadGoldStarIcon(map) {
  // Check if icon is already loaded
  if (map.hasImage("gold-star")) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    // Create a gold star SVG
    const svg = `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
  <path d="M15 0 L18.5 10.5 L30 12 L21.5 20 L23.5 30 L15 24 L6.5 30 L8.5 20 L0 12 L11.5 10.5 Z" 
        fill="#ffd700" 
        stroke="#000000" 
        stroke-width="1.5"/>
</svg>`;

    const img = new Image();
    img.onload = () => {
      if (!map.hasImage("gold-star")) {
        map.addImage("gold-star", img);
      }
      resolve();
    };
    img.onerror = () => {
      console.warn("Failed to load gold star icon");
      resolve();
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}

/**
 * Add island marker layer to map with all interactions.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} mapGeoJson - Full GeoJSON data
 * @param {Array} islands - Island CSV data
 * @param {string} islandIdField - CSV field for island ID
 * @param {Function} onIslandSelect - Callback when island is selected
 * @param {string|null} assignedIslandId - ID of the user's assigned island
 * @returns {Object} Object with cleanup function
 */
export function addIslandMarkers(
  map,
  mapGeoJson,
  islands,
  islandIdField,
  onIslandSelect,
  assignedIslandId = null
) {
  removeExistingLayer(
    map,
    ["islands-layer", "assigned-island-layer", "islands-labels"],
    "islands"
  );

  const geoJsonData = filterIslandFeatures(mapGeoJson, assignedIslandId);

  map.addSource("islands", {
    type: "geojson",
    data: geoJsonData
  });

  // Add circle markers for regular islands first
  map.addLayer(createIslandLayer("islands"));

  // Add star markers for assigned island (only if icon is loaded)
  if (map.hasImage("gold-star")) {
    map.addLayer(createAssignedIslandLayer("islands"));
  }

  // Add native Mapbox labels layer (will use same source)
  map.addLayer(createIslandLabelLayer("islands"));

  // Setup click handlers for circle markers, assigned island markers, and labels
  const handleIslandClick = (feature) => {
    const islandData = matchIslandWithCsvData(feature, islands, islandIdField);
    onIslandSelect(islandData);
  };

  setupIslandClickHandler(map, "islands-layer", handleIslandClick);
  setupIslandClickHandler(map, "assigned-island-layer", handleIslandClick);
  setupIslandClickHandler(map, "islands-labels", handleIslandClick);

  // Setup hover handlers for cursor change only
  const cleanup1 = setupIslandHoverHandlers(map, "islands-layer");
  const cleanup2 = setupIslandHoverHandlers(map, "assigned-island-layer");
  setupIslandHoverHandlers(map, "islands-labels");

  // Return combined cleanup function
  const cleanup = {
    cleanup: () => {
      cleanup1.cleanup();
      cleanup2.cleanup();
    }
  };

  return cleanup;
}
